import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import type { IssueMapResult } from '../../../shared/helpers/issues-tab';
import { v1URL, v2URL } from 'src/app/shared/constants/urls.constants';
import { CommonService } from 'src/app/shared/services/common.service';
import { AppHttpService } from 'src/app/shared/services/app-http.service';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { map } from 'rxjs/operators';
import {
  ArcgisPreflight, ArcgisPushSaveResponse, ArcgisPushStatus, ArcgisPushTargetInput,
} from 'src/app/shared/models/arcgis-push';
import {
  IssueContractorUsersResponse,
  IssueResponse,
  IssueWorkflowActionsResponse,
  IssueWorkflowHistoryResponse,
} from 'src/app/shared/helpers/issue-workflow';

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  closeCreateDailyReportDialog: Subject<void> = new Subject<void>();
  filteredProjectsForMap: Subject<any> = new Subject<any>();
  removeActionFieldsFromForm: Subject<any> = new Subject<any>();

  actingRole: Subject<any> = new Subject<any>();
  changeView: Subject<any> = new Subject<any>();
  changeRecordView: Subject<any> = new Subject<any>();
  mediaData: Subject<any> = new Subject<any>();
  addValueToParentForm: Subject<any> = new Subject<any>();
  totalAmount: Subject<any> = new Subject<any>();
  stopLoading: Subject<any> = new Subject<any>();
  totalAdditionalWorkingDays: Subject<any> = new Subject<any>();
  error: Subject<any> = new Subject<any>();
  checkForErrors: Subject<any> = new Subject<any>();
  openReport: Subject<any> = new Subject<any>();
  openRightPanel: Subject<any> = new Subject<any>();
  /**
   * An issue pin of the project Issues tab (`_kind: 'issue'`) picked on the
   * header map: the Issues tab opens it in the right panel, as a row click does.
   */
  issueMapPinSelected = new Subject<{ id: string } & Record<string, unknown>>();
  /**
   * The project map's latest GET /v1/issue/for-map result while the Issues
   * tab is open (null otherwise): the tab tags its no-location rows from it.
   */
  issuesMapResult = new BehaviorSubject<IssueMapResult | null>(null);
  closeRightPanel: Subject<void> = new Subject<void>();
  /** The right panel has finished closing (the wrapper's route update done, its content gone): the next one may open */
  rightPanelClosed: Subject<void> = new Subject<void>();
  /** "Show on map" from a placed drawing: the project map turns this design layer on and zooms to it. */
  showMapLayer: Subject<string> = new Subject<string>();
  punchlistChanged: Subject<void> = new Subject<void>();
  dailyDiaryChanged: Subject<void> = new Subject<void>();
  // Fired by the floating + in projects-wrapper; the diary list opens the
  // create panel (it is the component that holds projectId/siteId).
  createDailyDiary: Subject<void> = new Subject<void>();
  /**
   * Which diary component the floating + was used to add ('weather' |
   * 'remark'). The daily report's + works the same way — it opens a menu of
   * components and the report screen opens the matching panel.
   */
  addDailyDiaryComponent: Subject<string> = new Subject<string>();
  /**
   * Whether the day open in the diary record view holds a diary. The floating
   * + needs it: on a day with no diary there is nothing to add a component
   * to, so it must offer to create the diary instead.
   */
  diaryDayHasRecord: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  closeReportPopup: Subject<any> = new Subject<any>();
  createRequest: Subject<any> = new Subject<any>();
  isAnyFormDirty: BehaviorSubject<any> = new BehaviorSubject<any>(false);
  nextActions: Subject<any> = new Subject<any>();
  addContractorStatusSuccess: Subject<any> = new Subject<any>();
  changeCurrentPageNumber: Subject<any> = new Subject<any>();

  constructor(
    private commonServices: CommonService,
    private appHttpService: AppHttpService,
    private http: HttpClient,
  ) {}

  fetchProjectsList(payload: any): Observable<any> {
    const urlConfig = v1URL.project;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchDailyReports(payload: any): Observable<any> {
    const urlConfig = v1URL.dailyReports.fetchByProject;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchProjectDetails(payload: any): Observable<any> {
    const urlConfig = v1URL.getProjectDetailsById;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  updateProject(payload: any, qp: any = {}): Observable<any> {
    const urlConfig = v1URL.project.update;
    return this.appHttpService.postService(urlConfig.url(), payload, qp);
  }

  fetchProjectDocuments(payload: any): Observable<any> {
    const urlConfig = v1URL.documents.folder.fetchAll;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createFolder(payload: any): Observable<any> {
    const urlConfig = v1URL.documents.folder.createFolder;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  deleteFolder(payload: any): Observable<any> {
    const urlConfig = v1URL.documents.folder.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  updateFolderName(payload: any): Observable<any> {
    const urlConfig = v1URL.documents.folder.updateName;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  updateDocumentName(payload: any): Observable<any> {
    const urlConfig = v1URL.documents.file.updateName;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  addDocument(payload: any): Observable<any> {
    const urlConfig = v1URL.documents.file.add;
    const formData = payload.imageFormData || new FormData();

    // Append folder_id and folder_path to FormData if they exist
    if (payload.folder_id) {
      formData.append('folder_id', payload.folder_id);
    }
    if (payload.folder_path) {
      formData.append('folder_path', payload.folder_path);
    }

    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), formData, {
      headers: new HttpHeaders({}),
    });
  }

  deleteDocument(payload: any): Observable<any> {
    const urlConfig = v1URL.documents.file.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createDailyReport(payload: any): Observable<any> {
    const { siteId, ...params } = payload;
    const urlConfig = v1URL.dailyReport.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ siteId }), params);
  }

  fetchProjectReports(payload: any): Observable<any> {
    const urlConfig = v1URL.projectReports.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchPayappItems(payload: any): Observable<any> {
    const urlConfig = v1URL.payapp.fetchPayappItems;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchPayapps(payload: any): Observable<any> {
    const { projectId, qp, ...params } = payload;
    const urlConfig = v1URL.payapp.fetchPayapps;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, qp }), params.data);
  }

  fetchPayappDetails(payload: any): Observable<any> {
    const { payappId, projectId, qp } = payload;
    const urlConfig = v1URL.payapp.fetchPayappDetails;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, payappId, qp }));
  }

  fetchPayAppSummary(payload: any): Observable<any> {
    const { projectId, payAppId } = payload;
    const urlConfig = v2URL.payAppSummary.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, payAppId }));
  }

  generatePayapp(payload: any): Observable<any> {
    const { projectId, dryRun, ...params } = payload;
    const urlConfig = v1URL.payapp.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, dryRun }), params.data);
  }

  fetchMohBalances(projectId: string): Observable<any> {
    const urlConfig = v1URL.payapp.fetchMohBalances;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }));
  }

  validatePayapp(projectId: string, payload: any): Observable<any> {
    const urlConfig = v1URL.payapp.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, dryRun: true }), payload);
  }

  uploadMohAttachment(projectId: string, payappId: string, mohRowId: string, formData: FormData): Observable<any> {
    const urlConfig = v1URL.payapp.uploadMohAttachment;
    return this.commonServices.callAPI(
      urlConfig.method,
      urlConfig.url({ projectId, payappId, mohRowId }),
      formData,
      { headers: new HttpHeaders({}) }
    );
  }

  deleteMohAttachment(projectId: string, payappId: string, mohRowId: string, attachmentId: string): Observable<any> {
    const urlConfig = v1URL.payapp.deleteMohAttachment;
    return this.commonServices.callAPI(
      urlConfig.method,
      urlConfig.url({
        projectId, payappId, mohRowId, attachmentId,
      })
    );
  }

  deletePayapp(projectId: string, payappId: string): Observable<any> {
    const urlConfig = v1URL.payapp.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, payappId }));
  }

  updatePayappStatus(payload: any): Observable<any> {
    const { dailyReportId, reportBidItemId, ...params } = payload;
    const urlConfig = v1URL.payapp.updatePayappStatus;
    return this.commonServices.callAPI(
      urlConfig.method,
      urlConfig.url({ dailyReportId, reportBidItemId }),
      params.data
    );
  }

  // Billing Settings
  fetchBillingSettings(payload: any): Observable<any> {
    const urlConfig = v2URL.billingSettings.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  updateBillingSettings(payload: any): Observable<any> {
    const { projectId, ...settings } = payload;
    const urlConfig = v2URL.billingSettings.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }), settings);
  }

  // Project Calendar
  fetchProjectCalendar(payload: { projectId: string }): Observable<any> {
    const urlConfig = v2URL.projectCalendar.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  updateProjectCalendar(payload: { projectId: string; calendars: any }): Observable<any> {
    const urlConfig = v2URL.projectCalendar.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.calendars);
  }

  initializeProjectCalendar(payload: { projectId: string }): Observable<any> {
    const urlConfig = v2URL.projectCalendar.initialize;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchSiteWiseDailyReportSummary(payload: any): Observable<any> {
    const urlConfig = v2URL.siteWiseDailyReportSummary.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  // Project Archive
  // list/create go through the serializer (response body is the {status,data,type} envelope);
  // download returns a plain `{url}` body, so it is intentionally left unwrapped.
  getProjectArchives(projectId: string): Observable<any> {
    const urlConfig = v2URL.projectArchive.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }))
      .pipe(map((res: any) => res?.data));
  }

  requestProjectArchive(projectId: string): Observable<any> {
    const urlConfig = v2URL.projectArchive.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }))
      .pipe(map((res: any) => res?.data));
  }

  getProjectArchiveDownloadUrl(projectId: string, archiveId: string): Observable<any> {
    const urlConfig = v2URL.projectArchive.download;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, archiveId }));
  }

  // ArcGIS field-record push. Plain JSON bodies (no serializer envelope).
  getArcgisPush(projectId: string): Observable<ArcgisPushStatus> {
    const urlConfig = v2URL.arcgisPush.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }));
  }

  saveArcgisPush(projectId: string, body: ArcgisPushTargetInput): Observable<ArcgisPushSaveResponse> {
    const urlConfig = v2URL.arcgisPush.save;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }), body);
  }

  testArcgisPush(projectId: string, body: Partial<ArcgisPushTargetInput>): Observable<ArcgisPreflight> {
    const urlConfig = v2URL.arcgisPush.test;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }), body);
  }

  retryArcgisPush(projectId: string): Observable<{ queued: number }> {
    const urlConfig = v2URL.arcgisPush.retry;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }), {});
  }

  backfillArcgisPush(projectId: string): Observable<{ queued: number }> {
    const urlConfig = v2URL.arcgisPush.backfill;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }), {});
  }

  fetchProjectMembers(payload: any): Observable<any> {
    const urlConfig = v1URL.projectMembers.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchProjectAccounts(payload: any): Observable<any> {
    const urlConfig = v1URL.projectAccounts.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchProjectInvitations(payload: any): Observable<any> {
    const urlConfig = v1URL.projectInvitations.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createProjectInvitation(payload: any): Observable<any> {
    const urlConfig = v1URL.projectInvitations.createForProject;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.body);
  }

  createSiteInvitation(payload: any): Observable<any> {
    const urlConfig = v1URL.projectInvitations.createForSite;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.body);
  }

  fetchProjectSitesForInvitation(payload: any): Observable<any> {
    const urlConfig = v1URL.projectSites.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchBidSheet(payload: any): Observable<any> {
    const urlConfig = v1URL.bidSheet.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchBidItems(payload: any): Observable<any> {
    const urlConfig = v1URL.bidItemsList.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchSettingsBidItemDetails(payload: { bidItemId: string; qp?: any }): Observable<any> {
    const urlConfig = v1URL.bidItem.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchBidItemFields(payload: any): Observable<any> {
    const urlConfig = v2URL.bidItem.fields;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchSettingsProjectMaterialsList(payload: { projectId: string; qp?: any }): Observable<any> {
    const urlConfig = v2URL.projectMaterials.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  addBidItemMaterial(payload: { materialId: string; bidItemId: string }): Observable<any> {
    const urlConfig = v2URL.bidItemMaterial.add;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), {});
  }

  removeBidItemMaterial(payload: { materialId: string; bidItemId: string }): Observable<any> {
    const urlConfig = v2URL.bidItemMaterial.remove;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  updateBidItemSiteQuantity(payload: { bidItemId: string; siteId: string; quantity: number }): Observable<any> {
    const urlConfig = v2URL.bidItemSiteQuantity.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), {});
  }

  createBidItem(payload: { bidSheetId: string; body: any }): Observable<any> {
    const urlConfig = v1URL.bidItem.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.body);
  }

  updateBidItem(payload: { bidItemId: string; body: any }): Observable<any> {
    const urlConfig = v1URL.bidItem.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.body);
  }

  deleteBidItem(payload: { bidItemId: string }): Observable<any> {
    const urlConfig = v1URL.bidItem.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  uploadChangeOrderFile(payload: any): Observable<any> {
    const urlConfig = v1URL.bidSheet.upload;
    const formData: FormData = new FormData();

    formData.append('order_number', payload.order_number);
    formData.append('comments', payload.comment);
    formData.append('inputFile', payload.inputFile);

    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), formData, {
      headers: new HttpHeaders({}),
    });
  }

  uploadBidSheetCsv(payload: { bidSheetId: string; inputFile: File }): Observable<any> {
    const urlConfig = v1URL.bidSheet.upload;
    const formData = new FormData();
    formData.append('inputFile', payload.inputFile);
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), formData, {
      headers: new HttpHeaders({}),
    });
  }

  fetchBidItemsSitesList(payload: any): Observable<any> {
    const urlConfig = v1URL.bidItemSitesList.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchChangeOrderFilter(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrders.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchBidItemListWithChangeOrder(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrders.fetchBidItemList;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchChangeOrderFilterInSideNav(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrders.fetchChangeOrderFiltersInContextOfBidItems;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchBidItemListWithChangeOrderInSideNav(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrders.fetchBidItemList;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchSiteDetails(payload: any): Observable<any> {
    const urlConfig = v1URL.siteDetails.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchDailyReport(payload: any): Observable<any> {
    const urlConfig = v1URL.dailyReport.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  /**
   * Full-body save of an existing daily report through the draft route. `baseUpdatedAt` (the
   * `updated_at` of the version this tab loaded) opts into the api's optimistic concurrency:
   * if the report changed since, the api writes nothing and answers 409 `report_changed`.
   */
  saveDailyReport(payload: any): Observable<any> {
    const urlConfig = v1URL.dailyReport.create;
    const { params, dailyReport, baseUpdatedAt } = payload;
    const body = baseUpdatedAt ? { ...dailyReport, base_updated_at: baseUpdatedAt } : dailyReport;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(params), body);
  }

  fetchIssueTypes(payload: any): Observable<any> {
    const urlConfig = v2URL.issueTypes.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchSubContractorFilter(payload: any): Observable<any> {
    const urlConfig = v1URL.subContractor.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchLaborFilter(payload: any): Observable<any> {
    const urlConfig = v1URL.labor.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchEquipmentFilter(payload: any): Observable<any> {
    const urlConfig = v1URL.equipment.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  updateIssuesDetails(payload: any): Observable<any> {
    const urlConfig = v1URL.issue.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  addIssuesDetails(payload: any): Observable<any> {
    const urlConfig = v1URL.issue.add;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(), payload);
  }

  changeIssueStatus(payload: any): Observable<any> {
    const urlConfig = v1URL.issue.changeStatus;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  executeCurrentActionStateRequest(payload: any): Observable<any> {
    const urlConfig = v1URL.workflow.execute;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), { input: payload.input });
  }

  fetchExternalTests(payload: any): Observable<any> {
    const urlConfig = v1URL.siteMaterialTest;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  addContractorStatus(payload: any): Observable<any> {
    const urlConfig = v1URL.dailyReport.addContractorStatus;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  downloadPayapp(projectId: string, payappId: string, payappName?: string, templateId?: string, format: string = 'pdf'): Observable<any> {
    const urlConfig = v1URL.payapp.download;
    const qp: any = {};
    if (templateId) qp.template_id = templateId;
    if (format && format !== 'pdf') qp.format = format;
    // Observe the full response so org-specific filenameConfig names (set server-side
    // via Content-Disposition) flow through to the downloaded file.
    return this.appHttpService.getService(
      urlConfig.url({ projectId, payappId }),
      qp,
      { responseType: 'blob', observe: 'response' },
    ).pipe(
      map((response: HttpResponse<Blob>) => {
        const blob = response.body;
        const extension = format === 'excel' ? 'xlsx' : 'pdf';
        let fileName = payappName ? `${payappName}.${extension}` : `payapp.${extension}`;
        const contentDisposition = response.headers?.get('Content-Disposition');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match && match[1]) {
            fileName = match[1].replace(/['"]/g, '').trim();
          }
        }
        saveAs(blob, fileName);
        return blob;
      })
    );
  }

  /**
   * Downloads a bid sheet's bid items as a CSV file and hands it to the browser.
   * The filename comes from the API's Content-Disposition header when it carries one,
   * otherwise "<project number or name> bidsheet.csv".
   */
  downloadBidSheetCsv(bidSheetId: string, projectLabel?: string): Observable<Blob> {
    const urlConfig = v1URL.bidSheet.downloadCsv;
    return this.appHttpService.getService(
      urlConfig.url({ bidSheetId }),
      {},
      { responseType: 'blob', observe: 'response' },
    ).pipe(
      map((response: HttpResponse<Blob>) => {
        const blob = response.body;
        const fileName = ProjectsService.resolveBidSheetCsvFileName(
          response.headers?.get('Content-Disposition'),
          projectLabel,
        );
        saveAs(blob, fileName);
        return blob;
      })
    );
  }

  static resolveBidSheetCsvFileName(contentDisposition: string | null | undefined, projectLabel?: string): string {
    const match = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match?.[1]) {
      return match[1].replace(/['"]/g, '').trim();
    }
    const label = (projectLabel || '').trim();
    return label ? `${label} bidsheet.csv` : 'bidsheet.csv';
  }

  downloadMohLedger(projectId: string, projectName?: string): Observable<any> {
    const urlConfig = v1URL.payapp.mohLedgerDownload;
    return this.appHttpService.getService(
      urlConfig.url({ projectId }),
      {},
      { responseType: 'blob', observe: 'response' },
    ).pipe(
      map((response: HttpResponse<Blob>) => {
        const blob = response.body;
        let fileName = projectName
          ? `Material_On_Hand_Ledger_${projectName.replace(/[^a-z0-9]+/gi, '_')}.xlsx`
          : 'Material_On_Hand_Ledger.xlsx';
        const contentDisposition = response.headers?.get('Content-Disposition');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match && match[1]) {
            fileName = match[1].replace(/['"]/g, '').trim();
          }
        }
        saveAs(blob, fileName);
        return blob;
      })
    );
  }

  fetchIssues(payload: any): Observable<any> {
    const urlConfig = v1URL.issues;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  deleteVideoFromMuxDb(payload: any): Observable<any> {
    const urlConfig = v2URL.video.deleteVideoFromMuxDb;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchActivityIdsList(payload: any): Observable<any> {
    const urlConfig = v1URL.activityCode.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  uploadActivityIdsFile(payload: any): Observable<any> {
    const urlConfig = v1URL.activityCode.upload;
    const formData: FormData = new FormData();

    formData.append('inputFile', payload.inputFile);

    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), formData, {
      headers: new HttpHeaders({}),
    });
  }

  updateActivityIdsDetails(payload: any): Observable<any> {
    const urlConfig = v1URL.activityCode.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  deleteActivityIdsDetails(payload: any): Observable<any> {
    const urlConfig = v1URL.activityCode.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchBasisOfPayOptions(payload: any): Observable<any> {
    const urlConfig = v1URL.lookUp.basisOfPay.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchChangeInWorkCodeOptions(payload: any): Observable<any> {
    const urlConfig = v1URL.lookUp.changeInWorkCode.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchChangeInWorkRequestList(payload: any): Observable<any> {
    const urlConfig = v1URL.changeInWorkRequest.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchChangeOrdersRequestList(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  saveChangeInWorkRequest(payload: any): Observable<any> {
    const urlConfig = v1URL.changeInWorkRequest.save;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(), payload);
  }

  saveChangeOrdersRequest(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.save;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(), payload);
  }

  fetchChangeInWorkRequestWithId(payload: any): Observable<any> {
    const urlConfig = v1URL.changeInWorkRequest.fetchWithId;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchChangeOrdersRequestWithId(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.fetchWithId;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  sendChangeInWorkRequestReminder(payload: any): Observable<any> {
    const urlConfig = v1URL.changeInWorkRequest.sendReminder;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), {});
  }

  sendChangeOrdersRequestReminder(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.sendReminder;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), {});
  }

  executeWorkflowActionState(payload: any): Observable<any> {
    const urlConfig = v1URL.workflow.state.execute;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  fetchLineage(payload: any): Observable<any> {
    const urlConfig = v1URL.workflow.lineage;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchReport(payload: any): Observable<any> {
    return this.commonServices.callAPI('get', payload?.url);
  }

  downloadReport(payload: any): Observable<HttpResponse<Blob>> {
    // Observe the full HttpResponse so the effect can read Content-Disposition
    // to use server-provided filenames (e.g. org-specific naming from filenameConfig).
    return this.http.get(payload?.url, {
      responseType: 'blob',
      observe: 'response',
      headers: new HttpHeaders({
        Accept: 'application/pdf',
      }),
    });
  }

  deleteDailyReport(payload: any): Observable<any> {
    const urlConfig = v1URL.dailyReport.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  deleteCIWR(payload: any): Observable<any> {
    const urlConfig = v1URL.changeInWorkRequest.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  uploadSubmittalLogFile(payload: any): Observable<any> {
    const urlConfig = v1URL.submittals.upload;
    const formData: FormData = new FormData();

    formData.append('inputFile', payload.inputFile);

    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), formData, {
      headers: new HttpHeaders({}),
    });
  }

  createSubmittalsRegistryItem(payload: any): Observable<any> {
    const urlConfig = v1URL.submittals.log;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  createSubmittalsItem(payload: any): Observable<any> {
    const urlConfig = v1URL.submittals.create.item;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  executeSubmitWorkFlow(payload: any): Observable<any> {
    const urlConfig = v1URL.workflow.submit;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), { input: payload.input });
  }

  executeSubmitRFIWorkFlow(payload: any): Observable<any> {
    const urlConfig = v1URL.workflow.rfi.submit;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), { input: payload.input });
  }

  fetchSubmittalsRegistryList(payload: any): Observable<any> {
    const urlConfig = v1URL.submittals.registry;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  fetchSubmittalsItemsList(payload: any): Observable<any> {
    const urlConfig = v1URL.submittals.item;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  fetchSubmittalsItemDetailsWithId(payload: any): Observable<any> {
    const urlConfig = v1URL.submittals.fetchWithId;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  deleteRFIItem(payload: { id: string }): Observable<unknown> {
    const urlConfig = v1URL.rfis.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  deleteSubmittalsItem(payload: any): Observable<any> {
    const urlConfig = v1URL.submittals.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchUsersList(payload: any): Observable<any> {
    const urlConfig = v1URL.users.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  fetchRFINo(payload: any): Observable<any> {
    const urlConfig = v1URL.rfis.fetchNo;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchProjectMembersList(payload: any): Observable<any> {
    const urlConfig = v1URL.projectMembers.members;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchContractorsList(payload: any): Observable<any> {
    const urlConfig = v1URL.subContractor.fetchByProject;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  /**
   * Fetches external labs (material test labs) for a project.
   * GET v1/material-test-lab?start=0&total=1000&projectId=xxx
   * Pagination is handled on the frontend.
   */
  fetchMaterialTestLabList(payload: any): Observable<any> {
    const urlConfig = v1URL.materialTestLab.fetchList;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  /**
   * Creates an external lab (material test lab) for a project.
   * POST v1/material-test-lab/:projectId
   */
  createMaterialTestLab(payload: { projectId: string; body: any }): Observable<any> {
    const urlConfig = v1URL.materialTestLab.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload?.body);
  }

  /**
   * Updates an external lab (material test lab) for a project.
   * PUT v1/material-test-lab/:projectId
   */
  updateMaterialTestLab(payload: { projectId: string; body: any }): Observable<any> {
    const urlConfig = v1URL.materialTestLab.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload?.body);
  }

  fetchSitesList(payload: any): Observable<any> {
    const { projectId, qp } = payload;
    const params = {
      projectId,
      qp: qp ? { start: qp.start ?? 0, total: qp.total ?? 1000 } : undefined,
    };
    const urlConfig = v1URL.projectSites.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(params));
  }

  deleteSite(payload: any): Observable<any> {
    const urlConfig = v1URL.site.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createSite(payload: any): Observable<any> {
    const urlConfig = v1URL.site.create;
    const body = payload?.body || payload;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(), body);
  }

  updateSite(payload: any): Observable<any> {
    const urlConfig = v1URL.site.update;
    const body = payload?.body || payload;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), body);
  }

  createContractor(payload: any): Observable<any> {
    const urlConfig = v1URL.subContractor.create;
    const body = payload?.body || payload;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), body);
  }

  updateContractor(payload: any): Observable<any> {
    const urlConfig = v1URL.subContractor.update;
    const body = payload?.body || payload;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), body);
  }

  deleteContractor(payload: any): Observable<any> {
    const urlConfig = v1URL.subContractor.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchDeliveryTrackingFields(payload: any): Observable<any> {
    const urlConfig = v2URL.deliveryTrackingFields.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createDeliveryTrackingField(payload: any): Observable<any> {
    const urlConfig = v2URL.deliveryTrackingFields.create;
    const body = payload?.body ?? payload;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), body);
  }

  updateDeliveryTrackingField(payload: any): Observable<any> {
    const urlConfig = v2URL.deliveryTrackingFields.update;
    const body = payload?.body ?? payload;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), body);
  }

  deleteDeliveryTrackingField(payload: any): Observable<any> {
    const urlConfig = v2URL.deliveryTrackingFields.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  deleteField(fieldId: string): Observable<any> {
    const urlConfig = v2URL.field.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ fieldId }));
  }

  createBidItemField(bidItemId: string, body: any): Observable<any> {
    const urlConfig = v2URL.bidItem.createField;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ bidItemId }), body);
  }

  updateBidItemField(fieldId: string, body: any): Observable<any> {
    const urlConfig = v2URL.field.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ fieldId }), body);
  }

  fetchExistingMembersList(payload: any): Observable<any> {
    const urlConfig = v1URL.projectMembers.availableMembers;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchProjectRolesList(payload: any): Observable<any> {
    const urlConfig = v1URL.projectRoles.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  inviteUser(payload: any): Observable<any> {
    const urlConfig = v1URL.projectMembers.invite;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  updateUser(payload: any): Observable<any> {
    const urlConfig = v1URL.projectMembers.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  deleteUser(payload: any): Observable<any> {
    const urlConfig = v1URL.projectMembers.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  checkEmailExists(email: string): Observable<any> {
    const urlConfig = v1URL.account.checkEmail;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ email }));
  }

  checkPhoneExists(phone: string): Observable<any> {
    const urlConfig = v1URL.account.checkPhone;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ phone }));
  }

  fetchRFIsList(payload: any): Observable<any> {
    const urlConfig = v1URL.rfis.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  createRFI(payload: any): Observable<any> {
    const urlConfig = v1URL.rfis.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  fetchRFIItemDetailsWithId(payload: any): Observable<any> {
    const urlConfig = v1URL.rfis.fetchWithId;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  submitReviewers(payload: any): Observable<any> {
    const urlConfig = v1URL.submittals.submitReviewers;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  submitRfiReviewers(payload: any): Observable<any> {
    const urlConfig = v1URL.rfis.submitReviewers;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  fetchMaterialTestNameList(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.fetchTestNameList;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchProjectMaterialTestList(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.fetchTestNameList;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createProjectMaterialTest(payload: { projectId: string; name: string }): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), { name: payload.name });
  }

  updateProjectMaterialTest(payload: { projectId: string; testId: string; name: string }): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), { name: payload.name });
  }

  deleteProjectMaterialTest(payload: { projectId: string; testId: string }): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchLabNameList(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.fetchLabList;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  saveExternalTest(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.save;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  sendExternalTestToLab(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.sendForLabTest;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  acceptExternalTestLabSchedule(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.acceptLabSchedule;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  recordExternalTestLabTestDetails(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.record;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  updateExternalTestLabTestReport(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.updateReport;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  updateExternalTestLabTestResult(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.updateResult;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  cancelExternalTest(payload: any): Observable<any> {
    const urlConfig = v1URL.externalMaterialTest.cancel;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload);
  }

  // Pay App Templates - Organization level
  getPayAppTemplates(organizationId: string): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.fetchOrgTemplates;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ organizationId }));
  }

  getPayAppTemplate(organizationId: string, templateId: string): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.fetchOrgTemplate;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ organizationId, templateId }));
  }

  createPayAppTemplate(organizationId: string, template: any): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.createOrgTemplate;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ organizationId }), template);
  }

  updatePayAppTemplate(organizationId: string, templateId: string, template: any): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.updateOrgTemplate;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ organizationId, templateId }), template);
  }

  deletePayAppTemplate(organizationId: string, templateId: string): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.deleteOrgTemplate;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ organizationId, templateId }));
  }

  // Pay App Templates - Project level (includes org templates)
  getProjectPayAppTemplates(projectId: string): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.fetchProjectTemplates;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }));
  }

  createProjectPayAppTemplate(projectId: string, template: any): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.createProjectTemplate;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId }), template);
  }

  updateProjectPayAppTemplate(projectId: string, templateId: string, template: any): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.updateProjectTemplate;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, templateId }), template);
  }

  deleteProjectPayAppTemplate(projectId: string, templateId: string): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.deleteProjectTemplate;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ projectId, templateId }));
  }

  // Pay App Template Preview
  previewPayAppTemplate(templateId: string, payAppId?: string): Observable<any> {
    const urlConfig = v2URL.payAppTemplates.preview;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({ templateId, payAppId }));
  }

  // Project Lookups
  fetchProjectLookup(payload: any): Observable<any> {
    const urlConfig = v2URL.lookup.fetch;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createProjectLookup(payload: any): Observable<any> {
    const urlConfig = v2URL.lookup.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.body);
  }

  deleteProjectLookup(payload: any): Observable<any> {
    const urlConfig = v2URL.lookup.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createChangeOrderFromRequests(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.createFromRequests;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(), payload);
  }

  fetchAvailableCors(payload: any): Observable<any> {
    const urlConfig = v1URL.changeInWorkRequest.fetchAvailableCors;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchPendingBidItems(payload: any): Observable<any> {
    const urlConfig = v1URL.changeInWorkRequest.fetchPendingBidItems;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  linkRequestsToChangeOrder(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.linkRequests;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.body);
  }

  fetchLinkedRequests(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.linkedRequests;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  unlinkRequestFromChangeOrder(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.unlinkRequest;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  deleteChangeOrder(payload: any): Observable<any> {
    const urlConfig = v1URL.changeOrdersRequest.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  // Warranty
  fetchWarranties(payload: any): Observable<any> {
    const urlConfig = v1URL.warranty.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createWarranty(payload: any): Observable<any> {
    const urlConfig = v1URL.warranty.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  updateWarranty(payload: any): Observable<any> {
    const urlConfig = v1URL.warranty.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  deleteWarranty(payload: any): Observable<any> {
    const urlConfig = v1URL.warranty.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchWarrantyClaims(payload: any): Observable<any> {
    const urlConfig = v1URL.warranty.claims.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createWarrantyClaim(payload: any): Observable<any> {
    const urlConfig = v1URL.warranty.claims.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  fetchWarrantyClaimDetail(payload: any): Observable<any> {
    const urlConfig = v1URL.warranty.claims.get;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  warrantyClaimAction(payload: any): Observable<any> {
    // Map REST actions to workflow engine states
    const stateMap = {
      submit: 'claim-submit',
      approve: 'claim-send-contractor',
      verify: 'claim-verify',
      close: 'claim-verify-close',
      respond: 'claim-respond',
      'send-back': 'claim-send-back',
      reopen: 'claim-reopen',
    };

    const state = stateMap[payload.action];
    if (state) {
      // Use workflow engine — records lineage for Discussion tab
      const urlConfig = v1URL.workflow.execute;
      return this.commonServices.callAPI(urlConfig.method, urlConfig.url({
        qp: {
          wf: 'warranty-claim',
          ex: `warranty-claim/${payload.claimId}`,
          state,
        },
      }), {
        input: {
          id: payload.claimId,
          formValues: {
            comments: payload.data?.notes || '',
            attachments: payload.data?.pictures || [],
          },
        },
      });
    }

    // Fallback to REST for actions not in workflow
    const urlConfig = v1URL.warranty.claims[payload.action];
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  // Punchlist
  fetchPunchlist(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.get;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createPunchlist(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  updatePunchlist(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  deletePunchlist(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchPunchlistItems(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  createPunchlistItem(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.create;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  fetchPunchlistItem(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.get;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  updatePunchlistItem(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.update;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  deletePunchlistItem(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  /** Runs a punch list item workflow state picked from next-state with its formValues. */
  executePunchlistItemState(payload: { itemId: string; state: string; formValues: Record<string, any> }): Observable<any> {
    const urlConfig = v1URL.workflow.execute;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({
      qp: { wf: 'punchlist-item', ex: `punchlist-item/${payload.itemId}`, state: payload.state },
    }), {
      input: { id: payload.itemId, formValues: payload.formValues },
    });
  }

  /**
   * People the caller may Forward a punch list item to (the API decides who: anyone on the project for PM/owner; caller excluded).
   * Rows: { id (uuid), uuid, first_name, last_name, name, email, role_code, role_codes, sub_contractor }.
   */
  fetchPunchlistForwardCandidates(payload: { itemId: string }): Observable<any> {
    const urlConfig = v1URL.punchlist.items.forwardCandidates;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  // Issue contractor workflow (projects with issue_workflow_enabled). The
  // callers only make these requests for an issue of an enabled project.

  /** The issue with the API's default include (contractor_accounts, sub_accounts, project). */
  fetchIssue(payload: { issueId: string }): Observable<IssueResponse> {
    const urlConfig = v1URL.issue.get;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  /** The actions the user may take on the issue right now (role, stage, recipient, site). */
  fetchIssueWorkflowActions(payload: { issueId: string; projectId: string }): Observable<IssueWorkflowActionsResponse> {
    const urlConfig = v1URL.workflow.nextState;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({
      qp: { wf: 'issue', id: payload.issueId, project_id: payload.projectId },
    }));
  }

  /**
   * Runs an issue workflow state through the workflow engine, like
   * punchlistItemAction: the engine records it in the issue's lineage. The
   * response is the updated issue.
   */
  issueWorkflowAction(
    payload: { issueId: string; state: string; formValues: Record<string, unknown> },
  ): Observable<IssueResponse> {
    const urlConfig = v1URL.workflow.execute;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({
      qp: {
        wf: 'issue',
        state: payload.state,
        ex: `issue/${payload.issueId}`,
      },
    }), {
      input: {
        id: payload.issueId,
        formValues: payload.formValues || {},
      },
    });
  }

  /**
   * Who the caller may send / forward the issue to: everyone on the project
   * but the caller for a project manager or a non-contractor recipient, the
   * contractor users for a contractor.
   */
  fetchIssueContractorUsers(payload: { issueId: string }): Observable<IssueContractorUsersResponse> {
    const urlConfig = v1URL.issue.contractorUsers;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  /** The issue's history: the lineage store behind the punch list / warranty Discussion tab. */
  fetchIssueWorkflowHistory(payload: { issueId: string }): Observable<IssueWorkflowHistoryResponse> {
    const urlConfig = v1URL.workflow.lineage;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url({
      qp: {
        ex: `issue/${payload.issueId}`,
        start: 0,
        total: 50,
        include: 'actor,state,attachments',
      },
    }));
  }

  fetchPunchlistItemIssues(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.issues.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  addPunchlistItemIssues(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.issues.add;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  removePunchlistItemIssue(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.issues.remove;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchPunchlistParticipants(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.participants.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  addPunchlistParticipant(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.participants.add;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  removePunchlistParticipant(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.participants.remove;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchPunchlistContractorAccounts(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.contractorAccounts.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  addPunchlistContractorAccount(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.contractorAccounts.add;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  removePunchlistContractorAccount(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.contractorAccounts.remove;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchPunchlistItemSubAccounts(payload: any): Observable<any> {
    const urlConfig = v1URL.punchlist.items.subAccounts.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  // Daily Diary (AASHTOWare-specific lightweight reports — weather + remarks)
  fetchDailyDiaries(payload: { siteId: string; startDate?: string; endDate?: string }): Observable<any> {
    const urlConfig = v1URL.dailyDiary.list;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchDailyDiary(payload: { diaryId: string }): Observable<any> {
    const urlConfig = v1URL.dailyDiary.get;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  /** Conditions for a site on a date. Shape: `{ weather: {...} }`. */
  fetchSiteWeather(
    payload: { siteId: string; date: string },
  ): Observable<{ weather?: Record<string, unknown> }> {
    const urlConfig = v1URL.dailyDiary.weather;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  /**
   * Creates (no id) or updates a daily diary. `baseUpdatedAt` (the `updated_at` of the version
   * the form loaded; never on a create) opts into the api's optimistic concurrency: if the
   * diary changed since, the api writes nothing and answers 409 `report_changed`.
   */
  saveDailyDiaryDraft(payload: { siteId: string; data: any; baseUpdatedAt?: string | null }): Observable<any> {
    const urlConfig = v1URL.dailyDiary.saveDraft;
    const body = payload.baseUpdatedAt ? { ...payload.data, base_updated_at: payload.baseUpdatedAt } : payload.data;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), body);
  }

  submitDailyDiary(payload: { diaryId: string }): Observable<any> {
    const urlConfig = v1URL.dailyDiary.submit;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), {});
  }

  deleteDailyDiary(payload: { diaryId: string }): Observable<any> {
    const urlConfig = v1URL.dailyDiary.delete;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  addDailyDiaryRemark(payload: { diaryId: string; data: any }): Observable<any> {
    const urlConfig = v1URL.dailyDiary.addRemark;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload), payload.data);
  }

  deleteDailyDiaryRemark(payload: { diaryId: string; remarkId: string }): Observable<any> {
    const urlConfig = v1URL.dailyDiary.deleteRemark;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchDailyDiaryRemarkCategories(): Observable<any> {
    const urlConfig = v1URL.dailyDiary.remarkCategories;
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url());
  }

  scanBatchTicket(payload: { file: File }): Observable<any> {
    const urlConfig = v1URL.concreteBatchTicket.extract;
    const formData = new FormData();
    formData.append('file', payload.file, payload.file.name);
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(), formData, {
      headers: new HttpHeaders({}),
    });
  }
}
